// Client-side image compression for upload paths.
//
// Why this exists:
//   • iPhone library photos handed off through Mac Photos / iMessage are HEIC,
//     which the backend (Pillow without pillow-heif) rejects with HTTP 400.
//   • Modern phone photos are routinely 4–8MB; backend caps at 2MB so they
//     also bounce with 400.
//
// What it does:
//   • Decodes the source File via createImageBitmap (modern, fast) with an
//     HTMLImageElement fallback (Safari < 17 path).
//   • Downscales the longest side to maxEdge (default 1600px) — Kling itself
//     only needs short side ≥ 512, so 1600 is comfortably above quality floor.
//   • Re-encodes as JPEG at the given quality. The encoder runs in the
//     browser's native pipeline so HEIC → JPEG works wherever the decoder
//     supports HEIC (Safari macOS + iOS, all current versions).
//
// Returns a new File whose name ends in .jpg and whose type is image/jpeg.

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_QUALITY  = 0.85;

const looksLikeHeic = (file) =>
  /heic|heif/i.test(file.type || '') || /\.hei[cf]$/i.test(file.name || '');

const decodeToBitmap = async (file) => {
  // createImageBitmap is the fast path — but it can throw on HEIC in browsers
  // without HEIC support. Fall back to the legacy <img> path, which lets the
  // browser's image pipeline decide what to do (Safari decodes HEIC; Chrome
  // does not). Either way, downstream code handles the error.
  if (typeof createImageBitmap === 'function') {
    try { return await createImageBitmap(file); }
    catch { /* fall through */ }
  }
  return await new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode failed')); };
    img.src = url;
  });
};

export async function compressImage(file, opts = {}) {
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const quality = opts.quality ?? DEFAULT_QUALITY;

  // Skip work if it's already a small JPEG/PNG. Saves CPU on already-good
  // uploads (most retailer images, our generated avatars).
  const isSmallAndSupported =
    !looksLikeHeic(file) &&
    (file.type === 'image/jpeg' || file.type === 'image/png') &&
    file.size < 1_800_000;
  if (isSmallAndSupported) return file;

  let bitmap;
  try { bitmap = await decodeToBitmap(file); }
  catch {
    // Decoder couldn't read it (HEIC on Chrome, corrupted file, etc).
    // Surface a clear error so the caller can show actionable guidance
    // instead of pushing a broken upload at the server.
    const err = new Error(
      'This image format isn\'t supported here. Save the photo as JPEG or PNG and try again. ' +
      '(iPhone tip: Settings → Camera → Formats → Most Compatible.)'
    );
    err.code = 'DECODE_FAILED';
    throw err;
  }

  const srcW = bitmap.width  || bitmap.naturalWidth;
  const srcH = bitmap.height || bitmap.naturalHeight;
  const longest = Math.max(srcW, srcH);
  const scale   = longest > maxEdge ? maxEdge / longest : 1;
  const dstW = Math.round(srcW * scale);
  const dstH = Math.round(srcH * scale);

  const canvas = document.createElement('canvas');
  canvas.width = dstW;
  canvas.height = dstH;
  const ctx = canvas.getContext('2d');
  // White background so any transparency flattens to white instead of black
  // (Kling rejects PNG-on-black gunk too).
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, dstW, dstH);
  ctx.drawImage(bitmap, 0, 0, dstW, dstH);

  const blob = await new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('encode failed'))),
      'image/jpeg',
      quality,
    ),
  );

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' });
}
